import React, { useState } from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faCheck,
    faCircleCheck,
    faCircleXmark,
    faCommentSms,
    faCopy,
    faEnvelope,
    faFlask,
    faSparkles,
    faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import { FlatCard } from '@/components/FlatCard';
import { affectedCustomers, deliveries, deliveryFailures, moduleStatuses, ModuleState } from './customersDummyData';
import { linearIssues } from './activityDummyData';
import shared from './SystemHealthPage.module.scss';
import theme from './CustomersTab.module.scss';

const stateIcon: Record<ModuleState, IconDefinition> = {
    ok: faCircleCheck,
    degraded: faTriangleExclamation,
    down: faCircleXmark,
};

const stateLabel: Record<ModuleState, string> = {
    ok: 'Working',
    degraded: 'Partly affected',
    down: 'Down',
};

const severityLabel = { high: 'High', medium: 'Medium', low: 'Low' };

const fmt = (n: number) => n.toLocaleString('en-GB');
const pct = (failed: number, sent: number) => `${((failed / Math.max(1, sent)) * 100).toFixed(1)}%`;

function timeAgo(date: Date, now: number) {
    const m = Math.max(0, Math.round((now - date.getTime()) / 60_000));
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
}

const CustomersTab: React.FC<{ now: number }> = ({ now }) => {
    const affectedModules = moduleStatuses.filter((m) => m.state !== 'ok');
    const workingModules = moduleStatuses.filter((m) => m.state === 'ok');
    const customerModules = [...new Set(affectedCustomers.map((c) => c.module))];
    const affectedUsers = affectedCustomers.reduce((n, c) => n + c.users, 0);
    const shipped = linearIssues.filter((i) => i.state === 'Done');
    const coming = linearIssues.filter((i) => i.state === 'Ready for test' || i.state === 'In review');

    return (
        <div className={theme.tab}>
            <p className={theme.headline}>
                {affectedCustomers.length === 0 ? (
                    'Everything is working normally for customers.'
                ) : (
                    <>
                        <strong>{affectedCustomers.length} customers affected</strong> in{' '}
                        {customerModules.join(', ').replace(/, ([^,]*)$/, ' and $1')}, about {affectedUsers} users.
                    </>
                )}
            </p>

            {affectedModules.length > 0 && (
                <section className={theme.incidents}>
                    {affectedModules.map((m) => (
                        <div key={m.module} className={`${theme.module} ${theme[m.state]}`}>
                            <div className={theme.moduleHead}>
                                <FontAwesomeIcon icon={stateIcon[m.state]} className={theme.moduleIcon} />
                                <span className={theme.moduleName}>{m.module}</span>
                                <span className={theme.moduleState}>{stateLabel[m.state]}</span>
                            </div>
                            <p className={theme.moduleSummary}>{m.summary}</p>
                            {m.customerMessage && <CopyMessage text={m.customerMessage} />}
                        </div>
                    ))}
                </section>
            )}

            <section className={theme.workingRow}>
                <span className={theme.workingLabel}>Working normally</span>
                {workingModules.map((m) => (
                    <span key={m.module} className={theme.workingChip}>
                        <FontAwesomeIcon icon={faCircleCheck} /> {m.module}
                    </span>
                ))}
            </section>

            <div className={theme.row}>
                <FlatCard title="Affected customers">
                    <div className={theme.tableWrap}>
                        <table className={shared.table}>
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>What they see</th>
                                    <th className={shared.num}>Projects</th>
                                    <th className={shared.num}>Users</th>
                                    <th className={shared.num}>Since</th>
                                </tr>
                            </thead>
                            <tbody>
                                {affectedCustomers.map((c) => (
                                    <tr key={`${c.customer}-${c.module}`}>
                                        <td>
                                            <div className={theme.customer}>{c.customer}</div>
                                            <span className={`${theme.severity} ${theme[c.severity]}`}>
                                                {severityLabel[c.severity]}
                                            </span>
                                        </td>
                                        <td>
                                            {c.problem}
                                            <div className={shared.muted}>{c.module}</div>
                                        </td>
                                        <td className={shared.num}>{c.projects}</td>
                                        <td className={shared.num}>{c.users}</td>
                                        <td className={`${shared.num} ${shared.muted}`}>{timeAgo(c.since, now)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FlatCard>

                <FlatCard title="Emails and SMS (24h)">
                    <div className={theme.deliveryTiles}>
                        <DeliveryTile icon={faEnvelope} label="Email" {...deliveries.email} />
                        <DeliveryTile icon={faCommentSms} label="SMS" {...deliveries.sms} />
                    </div>
                    <h5 className={theme.subheading}>Failures by customer</h5>
                    <ul className={theme.failures}>
                        {deliveryFailures.map((f) => (
                            <li key={`${f.customer}-${f.channel}-${f.reason}`}>
                                <FontAwesomeIcon
                                    icon={f.channel === 'Email' ? faEnvelope : faCommentSms}
                                    className={theme.failureIcon}
                                    fixedWidth
                                />
                                <div className={theme.failureBody}>
                                    <div>
                                        <strong>{f.customer}</strong> · {f.reason}
                                    </div>
                                    <div className={shared.muted}>
                                        {f.channel}, last {timeAgo(f.lastAt, now)}
                                    </div>
                                </div>
                                <span className={theme.failureCount}>{f.count}</span>
                            </li>
                        ))}
                    </ul>
                </FlatCard>
            </div>

            <div className={theme.row}>
                <FlatCard
                    title={
                        <span className={theme.cardTitle}>
                            <FontAwesomeIcon icon={faSparkles} /> Shipped this week
                        </span>
                    }>
                    <ReleaseList issues={shipped} now={now} />
                </FlatCard>
                <FlatCard
                    title={
                        <span className={theme.cardTitle}>
                            <FontAwesomeIcon icon={faFlask} /> Coming soon
                        </span>
                    }>
                    <p className={theme.cardNote}>Being tested now; likely live within a few days.</p>
                    <ReleaseList issues={coming} now={now} />
                </FlatCard>
            </div>
        </div>
    );
};

const CopyMessage: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () =>
        navigator.clipboard
            ?.writeText(text)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {});
    return (
        <div className={theme.message}>
            <p className={theme.messageText}>“{text}”</p>
            <Button size="xs" variant="secondary" icon={copied ? faCheck : faCopy} onClick={copy}>
                {copied ? 'Copied' : 'Copy message for customers'}
            </Button>
        </div>
    );
};

const DeliveryTile: React.FC<{ icon: IconDefinition; label: string; sent: number; failed: number }> = ({
    icon,
    label,
    sent,
    failed,
}) => {
    const bad = failed / Math.max(1, sent) > 0.01;
    return (
        <div className={shared.stat}>
            <div className={shared.statLabel}>
                <FontAwesomeIcon icon={icon} /> {label}
            </div>
            <div className={shared.statValue}>{fmt(sent)}</div>
            <div className={bad ? theme.failedBad : shared.muted}>
                {fmt(failed)} failed ({pct(failed, sent)})
            </div>
        </div>
    );
};

const ReleaseList: React.FC<{ issues: typeof linearIssues; now: number }> = ({ issues, now }) =>
    issues.length === 0 ? (
        <p className={shared.muted}>Nothing yet.</p>
    ) : (
        <ul className={theme.releases}>
            {issues.map((i) => (
                <li key={i.id}>
                    <span className={theme.releaseTitle}>{i.title}</span>
                    <span className={shared.muted}>
                        {i.id} · {timeAgo(i.updatedAt, now)}
                    </span>
                </li>
            ))}
        </ul>
    );

export default CustomersTab;
